import { ReactElement } from 'react';
import { isValid, parseISO } from 'date-fns';
import FormControl, { FormControlProps } from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import DateFnsUtils from '@date-io/date-fns';
import {
  DatePicker,
  DatePickerProps,
  DatePickerView,
  DatePickerViewsProps,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';

import { ReactComponent as CalendarIcon } from '~/ui/assets/images/calendarIcon.svg';

import variables from '~/ui/assets/styles/colors.module.scss';
import styles from './DatePicker.module.scss';

interface IProps {
  size?: FormControlProps['size'];
  label?: string;
  error?: string;
  openTo?: DatePickerViewsProps['openTo'];
  views?: DatePickerView[];
  minDate?: string;
  maxDate?: string;
  dateFormat?: string;
  value: Date | string;
  onChange: (date: Date | string) => void;
  disabled?: boolean;
  placeholder?: string;
  clearable?: boolean;
  className?: string;
  cb?: (value: Date) => void;
  tabIndex?: number;
  showIcon?: boolean;
  fullWidth?: boolean;
  muiProps?: Partial<DatePickerProps>;
}

const DatePickerMUI = ({
  size,
  label,
  error,
  openTo,
  views,
  minDate,
  maxDate,
  dateFormat,
  value,
  onChange,
  tabIndex = 0,
  disabled,
  placeholder,
  clearable,
  className,
  showIcon = true,
  fullWidth = true,
  cb,
  muiProps,
}: IProps): ReactElement => {
  const isError = Boolean(error);

  const handleChange = (date: [Date, Date] | Date) => {
    if (Array.isArray(date) || !isValid(date)) return '';

    return new Date(date.setHours(12)).toISOString();
  };

  const formatValue = (val: Date | string) => {
    if (val instanceof Date) return isValid(val) ? val : null;
    const date = parseISO(val);
    if (!isValid(date)) return null;
    return date;
  };

  return (
    <FormControl
      variant="outlined"
      size={size}
      fullWidth={fullWidth}
      className={styles.datePickerControl}
    >
      {label && (
        <InputLabel shrink error={isError}>
          {label}
        </InputLabel>
      )}
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <DatePicker
          InputProps={{
            tabIndex,
            endAdornment: showIcon ? <CalendarIcon color={variables.colorRed} /> : undefined,
          }}
          error={!!error}
          openTo={openTo}
          views={views}
          fullWidth
          minDate={minDate || undefined}
          maxDate={maxDate || undefined}
          inputVariant="outlined"
          size={size}
          format={dateFormat}
          value={formatValue(value)}
          onChange={val => {
            cb?.(val);
            onChange(handleChange(val));
          }}
          disabled={disabled}
          placeholder={placeholder}
          clearable={clearable}
          className={className}
          DialogProps={{ disableRestoreFocus: true }}
          {...muiProps}
        />
      </MuiPickersUtilsProvider>

      {error && (
        <FormHelperText error className={styles.errorText}>
          {error.toString()}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default DatePickerMUI;
